import React, { useState, useRef } from 'react';

const SYSTEM_CONTEXT = `You are the FTI Procurement Intelligence Platform support assistant. Help users understand how to use the system. Here's what each section does:

**Dashboard** - Overview of all procurement activity. Shows total opportunities, upcoming deadlines, vendor stats, and recent activity.

**Opportunities** - Where you manage contract opportunities from SAM.gov.
- Upload CSV: Click "Upload CSV" to bulk-import opportunities from SAM.gov contract opportunities export files. The system auto-creates a lifecycle entry and procurement case for each.
- Create manually: Click "+ Create Opportunity" to add one manually.
- Click any opportunity to see full details, upload documents (RFP, SOW, RFI), and run AI analysis to extract requirements.
- The AI can analyze both uploaded documents AND the description text from the notice.

**Vendors** - Your awardee/vendor intelligence database.
- Upload CSV: Click "Upload CSV" to import vendor data from your award-based CSV files. Each row becomes a filterable entry.
- Filter: Use the column header filters to find vendors by sector, industry (NAICS), capabilities (PSC), contract ID, department, etc.
- Items stack one over the other in each cell and you can filter on individual items.
- Each week upload the new file - existing vendors update, new ones get added.

**Engagement** - Track vendor engagements, meetings, communications, and action items for each procurement case.

**Alerts** - Set up notification rules for deadlines, new opportunities matching criteria, and status changes.

**Lifecycle** - Track where each procurement is in the acquisition lifecycle:
- Intake → Market Research → Planning → Solicitation → Evaluation → Award → Post-Award
- Each uploaded opportunity gets auto-assigned a stage based on its type (Sources Sought = Market Research, Presolicitation = Planning, Solicitation = Solicitation, etc.)

**How to use the system weekly:**
1. Download latest contract opportunities from SAM.gov as CSV
2. Go to Opportunities → Upload CSV
3. Download latest vendor/award data
4. Go to Vendors → Upload CSV
5. Review opportunities in Lifecycle view to see what stage each is at
6. Open individual opportunities to run AI analysis on descriptions or uploaded documents
7. Use the AI chat at the bottom of any page to ask questions about your data

**Tips:**
- The AI assistant at the bottom of every page can answer questions about your procurement data
- You can delete opportunities and their associated lifecycle/case entries get cleaned up automatically
- Use the search bar on Opportunities and Vendors to quickly find what you need
- The Print/Export button in the top bar exports the current view as PDF
`;

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function Support() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setInput('');
    setLoading(true);

    // Simple local AI response based on context matching
    setTimeout(() => {
      const answer = generateAnswer(question);
      setMessages(prev => [...prev, { role: 'assistant', content: answer }]);
      setLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }, 500);
  };

  const generateAnswer = (q: string): string => {
    const ql = q.toLowerCase();

    if (ql.includes('upload') && ql.includes('opportunit')) {
      return "To upload opportunities:\n1. Download your contract opportunities CSV from SAM.gov\n2. Go to the **Opportunities** page\n3. Click the blue **Upload CSV** button\n4. Select your CSV file\n\nThe system will parse it, skip duplicates, and create a lifecycle entry + procurement case for each new opportunity.";
    }
    if (ql.includes('upload') && ql.includes('vendor')) {
      return "To upload vendors:\n1. Go to the **Vendors** page\n2. Click the green **Upload CSV** button\n3. Select your award-based vendor CSV file\n\nEach row becomes a separate entry. If you re-upload, existing vendors (by name) get updated and new ones are added.";
    }
    if (ql.includes('lifecycle') || ql.includes('stage')) {
      return "The **Lifecycle** page tracks where each procurement is in the acquisition process:\n\n• **Intake** — Special notices, initial awareness\n• **Market Research** — Sources sought, RFI\n• **Planning** — Presolicitation\n• **Solicitation** — Active solicitations, RFQ/RFP\n• **Evaluation** — Evaluating proposals\n• **Award** — Contract awarded\n• **Post-Award** — Contract administration\n\nWhen you upload opportunities, the stage is auto-assigned based on the notice type.";
    }
    if (ql.includes('vendor') && ql.includes('filter')) {
      return "To filter vendors:\n• Use the **dropdown filters** in the column headers (Sector, Department, Subtier, Office)\n• Use the **text filter inputs** for Industry, Capabilities, Contract ID\n• The filter matches any text within the cell — so typing 'D318' finds any vendor with that PSC code\n• Click **Clear All Filters** to reset";
    }
    if (ql.includes('delete') && ql.includes('opportunit')) {
      return "To delete an opportunity:\n1. Go to the **Opportunities** list\n2. Click the trash icon next to the opportunity\n3. Confirm deletion\n\nThis automatically deletes the associated lifecycle entry and procurement case.";
    }
    if (ql.includes('analys') || ql.includes('requirement') || ql.includes('ai')) {
      return "To run AI analysis on an opportunity:\n1. Open the opportunity detail page\n2. Go to the **Requirements** tab\n3. Either upload a document (PDF, DOCX) and click **Extract Requirements**, or\n4. Click **Analyze from Description** to have AI extract requirements from the notice description text\n\nThe AI will identify key requirements, evaluation criteria, deadlines, and compliance needs.";
    }
    if (ql.includes('engagement')) {
      return "The **Engagement** page tracks your interactions with vendors and procurement stakeholders:\n• Log meetings, calls, and emails\n• Track action items and follow-ups\n• Associate engagements with specific opportunities\n• Set reminders for follow-up dates";
    }
    if (ql.includes('alert')) {
      return "The **Alerts** page lets you set up notification rules:\n• Get notified when response deadlines approach\n• Track new opportunities matching your NAICS codes\n• Monitor status changes on watched opportunities\n• Check alerts to see what needs attention today";
    }
    if (ql.includes('dashboard')) {
      return "The **Dashboard** gives you an overview of:\n• Total active opportunities and upcoming deadlines\n• Vendor database stats\n• Recent uploads and activity\n• Quick links to items needing attention\n\nIt's your daily starting point to see what's happening across all procurement activity.";
    }
    if (ql.includes('weekly') || ql.includes('workflow') || ql.includes('how to use')) {
      return "**Weekly workflow:**\n\n1. **Download** latest contract opportunities from SAM.gov as CSV\n2. **Upload** to Opportunities page → Upload CSV\n3. **Download** latest vendor/award data\n4. **Upload** to Vendors page → Upload CSV\n5. **Review** Lifecycle page to see each procurement's stage\n6. **Analyze** individual opportunities — run AI on descriptions or uploaded docs\n7. **Track** deadlines and follow up on engagements\n\nThe system deduplicates automatically, so uploading the same file twice won't create duplicates.";
    }
    if (ql.includes('print') || ql.includes('export') || ql.includes('pdf')) {
      return "To export/print any page:\n• Click the **Export** button in the top-right corner\n• This opens your browser's print dialog\n• Choose \"Save as PDF\" to export as a document\n• The sidebar and navigation are automatically hidden in the export";
    }

    return "I can help you with:\n\n• **How to upload opportunities** from SAM.gov\n• **How to upload vendors** from award data\n• **How filtering works** in the vendor table\n• **How the lifecycle stages** work\n• **How to run AI analysis** on opportunities\n• **Weekly workflow** guide\n• **How each page/tab works**\n\nJust ask me a specific question!";
  };

  const quickQuestions = [
    'How do I upload opportunities?',
    'How do I upload vendors?',
    'How does the lifecycle work?',
    'How to filter vendors?',
    'How to run AI analysis?',
    'What is the weekly workflow?',
  ];

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, marginBottom: 4, fontWeight: 700 }}>Support</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>Ask questions about how to use the system</p>

      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {/* Messages */}
        <div style={{ minHeight: 400, maxHeight: 500, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>💬</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#475569', marginBottom: 6 }}>How can I help?</div>
              <div style={{ fontSize: 12, marginBottom: 20 }}>Ask me anything about using the FTI Procurement system</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {quickQuestions.map(q => (
                  <button
                    key={q}
                    onClick={() => { setInput(q); }}
                    style={{ fontSize: 11, padding: '6px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 20, cursor: 'pointer', color: '#475569', fontWeight: 500, transition: 'all 0.15s' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '85%',
                padding: '10px 14px',
                borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                background: msg.role === 'user' ? '#6366f1' : '#f1f5f9',
                color: msg.role === 'user' ? 'white' : '#1e293b',
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
              }}>
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ padding: '10px 14px', borderRadius: '14px 14px 14px 4px', background: '#f1f5f9', fontSize: 13, color: '#64748b' }}>
                Thinking...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, padding: '14px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask a question about the system..."
            aria-label="Ask a question about the system"
            style={{ flex: 1, padding: '11px 16px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none' }}
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            style={{ padding: '11px 20px', background: loading || !input.trim() ? '#c7d2fe' : '#6366f1', color: 'white', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: loading || !input.trim() ? 'default' : 'pointer', transition: 'all 0.15s' }}
          >
            Send
          </button>
        </form>
      </div>

      {/* Contact Support Team */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginTop: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>Contact Support Team</h3>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>Can't find what you need? Send a message directly to our team.</p>
        <form onSubmit={async (e) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          const subject = (form.elements.namedItem('subject') as HTMLInputElement).value;
          const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
          if (!subject || !message) return;
          setSendingEmail(true);
          setSendResult(null);
          try {
            const { api: supportApi } = await import('../api/client');
            await supportApi.post('/support-email', { subject, message, to: 'jordan@firstoinvest.com' });
            setSendResult('sent');
            form.reset();
          } catch {
            // Fallback: open mailto link
            window.location.href = `mailto:jordan@firstoinvest.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
            setSendResult('sent');
          }
          setSendingEmail(false);
        }}>
          <div style={{ marginBottom: 12 }}>
            <input name="subject" placeholder="Subject" aria-label="Subject" style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} required />
          </div>
          <div style={{ marginBottom: 12 }}>
            <textarea name="message" placeholder="Describe your question or issue..." aria-label="Describe your question or issue" rows={4} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} required />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="submit" disabled={sendingEmail} style={{ padding: '10px 20px', background: sendingEmail ? '#c7d2fe' : '#6366f1', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: sendingEmail ? 'default' : 'pointer' }}>
              {sendingEmail ? 'Sending...' : 'Send to Support'}
            </button>
            {sendResult === 'sent' && <span style={{ fontSize: 12, color: '#10b981', fontWeight: 500 }}>Message sent!</span>}
          </div>
        </form>
      </div>
    </div>
  );
}
